import React from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Checkbox,
    Column,
    Columns,
    Dialog,
    Heading,
    IconRefresh,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
} from "braid-design-system";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import { getAllItemGroups, ItemGroupResponse } from "../services/itemGroupService";
import {
    cancelStockInit,
    getStockInitStatus,
    initializeStock,
    StockInitStatus,
} from "../services/stockAdjustmentService";

const formatDateTime = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : "-";

const StockInitialization: React.FC = () => {
    const toast = useToast();

    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);

    const [status, setStatus] = React.useState<StockInitStatus | null>(null);
    const [groups, setGroups] = React.useState<ItemGroupResponse[]>([]);
    const [selectedCodes, setSelectedCodes] = React.useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = React.useState("");
    const [confirmAction, setConfirmAction] = React.useState<"initialize" | "cancel" | null>(null);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [initStatus, itemGroups] = await Promise.all([getStockInitStatus(), getAllItemGroups()]);
            setStatus(initStatus);
            setGroups(
                itemGroups
                    .filter((group) => group.group_code)
                    .sort((a, b) => String(a.group_code).localeCompare(String(b.group_code))),
            );
            setSelectedCodes(new Set());
        } catch (error: any) {
            setLoadError(error?.message || "Failed to load stock initialization");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const groupNameByCode = React.useMemo(
        () => new Map(groups.map((group) => [String(group.group_code), group.group_name || ""])),
        [groups],
    );

    const filteredGroups = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return groups;
        return groups.filter(
            (group) =>
                String(group.group_code).toLowerCase().includes(term) ||
                (group.group_name || "").toLowerCase().includes(term),
        );
    }, [groups, searchTerm]);

    const allSelected = groups.length > 0 && selectedCodes.size === groups.length;
    const filteredSelectedCount = filteredGroups.filter((group) => selectedCodes.has(String(group.group_code))).length;
    const headerChecked: boolean | "mixed" =
        filteredGroups.length > 0 && filteredSelectedCount === filteredGroups.length
            ? true
            : filteredSelectedCount > 0
              ? "mixed"
              : false;

    const toggleGroup = (code: string) => {
        setSelectedCodes((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const toggleFilteredGroups = () => {
        setSelectedCodes((prev) => {
            const next = new Set(prev);
            const selectAll = headerChecked !== true;
            filteredGroups.forEach((group) => {
                const code = String(group.group_code);
                if (selectAll) next.add(code);
                else next.delete(code);
            });
            return next;
        });
    };

    const handleInitialize = async () => {
        setSubmitting(true);
        try {
            const result = await initializeStock(Array.from(selectedCodes), allSelected);
            setStatus(result);
            setSelectedCodes(new Set());
            toast.showSuccess(
                `Stock initialized as batch ${result.open_init?.init_no} (${result.open_init?.initialized_lot_count} item lots).`,
            );
        } catch (error: any) {
            toast.showError(error?.message || "Failed to initialize stock");
        } finally {
            setSubmitting(false);
            setConfirmAction(null);
        }
    };

    const handleCancel = async () => {
        const initNo = status?.open_init?.init_no;
        if (!initNo) return;
        setSubmitting(true);
        try {
            setStatus(await cancelStockInit(initNo));
            toast.showSuccess(`Stock initialization ${initNo} cancelled.`);
        } catch (error: any) {
            toast.showError(error?.message || "Failed to cancel stock initialization");
        } finally {
            setSubmitting(false);
            setConfirmAction(null);
        }
    };

    if (loading) {
        return <Loading message="Please wait, loading stock initialization..." />;
    }

    if (loadError) {
        return (
            <Box padding="large">
                <Text tone="critical">{loadError}</Text>
            </Box>
        );
    }

    const openInit = status?.open_init ?? null;
    // The batch location is the ADJ setup's stock location (pos_trasetup.stocklocation).
    const locationLabel = status?.location_name || status?.location || "";

    return (
        <Box padding="medium">
            <Stack space="large">
                <Columns space="large" collapseBelow="tablet" alignY="center">
                    <Column>
                        <Inline space="small" alignY="center">
                            <Heading level="2">Stock Initialization</Heading>
                            {status?.location ? <Badge tone="info">{`Location ${locationLabel}`}</Badge> : null}
                        </Inline>
                    </Column>
                    <Column width="content">
                        <Button variant="ghost" tone="brandAccent" icon={<IconRefresh />} onClick={loadData}>
                            Refresh
                        </Button>
                    </Column>
                </Columns>

                {openInit ? (
                    <Stack space="medium">
                        <Alert tone="caution">
                            <Stack space="small">
                                <Text weight="strong">{`Batch ${openInit.init_no} is open at ${locationLabel}`}</Text>
                                <Text size="small">
                                    {`Initialized ${formatDateTime(openInit.init_date)} - ${openInit.initialized_lot_count} item lots, `}
                                    {`${openInit.processed_note_count} adjustment note(s) processed so far.`}
                                </Text>
                                <Text size="small">
                                    Enter counted stock through Stock Adjustment notes, then confirm them in Process
                                    Adjustment. A new initialization can only start after this batch is processed or
                                    cancelled.
                                </Text>
                            </Stack>
                        </Alert>

                        <Stack space="small">
                            <Text weight="strong">Initialized item groups</Text>
                            <Inline space="small">
                                {openInit.group_codes.map((code) => (
                                    <Badge key={code} tone="neutral">
                                        {groupNameByCode.get(code) ? `${code} - ${groupNameByCode.get(code)}` : code}
                                    </Badge>
                                ))}
                            </Inline>
                        </Stack>

                        <Box>
                            <Inline space="small" alignY="center">
                                <Button
                                    tone="critical"
                                    variant="soft"
                                    loading={submitting}
                                    onClick={
                                        openInit.processed_note_count > 0 || submitting
                                            ? undefined
                                            : () => setConfirmAction("cancel")
                                    }
                                >
                                    Cancel Initialization
                                </Button>
                                {openInit.processed_note_count > 0 ? (
                                    <Text size="small" tone="secondary">
                                        Processed notes exist for this batch, so it can no longer be cancelled.
                                    </Text>
                                ) : null}
                            </Inline>
                        </Box>
                    </Stack>
                ) : (
                    <Stack space="medium">
                        <Text tone="secondary">
                            Select the item groups to count. The current stock in hand of every item lot in those
                            groups at this location is snapshotted, and only those items can be entered on Stock
                            Adjustment notes until the batch is processed.
                        </Text>

                        <Columns space="medium" collapseBelow="tablet" alignY="center">
                            <Column>
                                <TextField
                                    id="stock-init-group-search"
                                    aria-label="Search item groups"
                                    placeholder="Search item groups..."
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.currentTarget.value)}
                                    onClear={() => setSearchTerm("")}
                                />
                            </Column>
                            <Column width="content">
                                <Inline space="small" alignY="center">
                                    <Text size="small" tone="secondary">
                                        {allSelected ? "All groups selected" : `${selectedCodes.size} of ${groups.length} selected`}
                                    </Text>
                                    <Button
                                        variant="solid"
                                        tone="formAccent"
                                        loading={submitting}
                                        onClick={selectedCodes.size === 0 || submitting ? undefined : () => setConfirmAction("initialize")}
                                    >
                                        Initialize Stock
                                    </Button>
                                </Inline>
                            </Column>
                        </Columns>

                        <Table label="Item groups">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell width="content">
                                        <Checkbox
                                            id="stock-init-select-all"
                                            label="Select all"
                                            checked={headerChecked}
                                            onChange={toggleFilteredGroups}
                                        />
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>Group Name</Text>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredGroups.length > 0 ? (
                                    filteredGroups.map((group) => {
                                        const code = String(group.group_code);
                                        return (
                                            <TableRow key={code}>
                                                <TableCell width="content">
                                                    <Checkbox
                                                        id={`stock-init-group-${code}`}
                                                        label={code}
                                                        checked={selectedCodes.has(code)}
                                                        onChange={() => toggleGroup(code)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Text>{group.group_name || "-"}</Text>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colspan={2} align="center">
                                            <Text tone="secondary">
                                                {searchTerm ? "No matching item groups." : "No item groups found."}
                                            </Text>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Stack>
                )}
            </Stack>

            <Dialog
                id="stock-init-confirm-dialog"
                title={confirmAction === "cancel" ? "Cancel stock initialization?" : "Initialize stock?"}
                open={confirmAction !== null}
                onClose={() => (submitting ? undefined : setConfirmAction(null))}
                width="small"
            >
                <Stack space="large">
                    {confirmAction === "cancel" ? (
                        <Text>
                            {`Batch ${openInit?.init_no} will be discarded and its ${openInit?.initialized_lot_count} item lots released. Stock in hand is not changed.`}
                        </Text>
                    ) : (
                        <Text>
                            {allSelected
                                ? `Initialize stock for all ${groups.length} item groups at ${locationLabel}?`
                                : `Initialize stock for ${selectedCodes.size} item group(s) at ${locationLabel}: ${Array.from(selectedCodes).sort().join(", ")}?`}
                        </Text>
                    )}
                    <Inline space="small" align="right">
                        <Button variant="ghost" tone="neutral" onClick={submitting ? undefined : () => setConfirmAction(null)}>
                            Back
                        </Button>
                        <Button
                            variant="solid"
                            tone={confirmAction === "cancel" ? "critical" : "formAccent"}
                            loading={submitting}
                            onClick={submitting ? undefined : confirmAction === "cancel" ? handleCancel : handleInitialize}
                        >
                            {confirmAction === "cancel" ? "Cancel Initialization" : "Initialize"}
                        </Button>
                    </Inline>
                </Stack>
            </Dialog>
        </Box>
    );
};

export default StockInitialization;
